import React from 'react';
import { Link } from 'react-router-dom';
import { TestTube, Globe, CreditCard, AlertCircle, CheckCircle, XCircle } from 'lucide-react';

export const Environments: React.FC = () => {
  const testCards = [
    {
      number: '5060990580000217499',
      expiry: '03/50',
      cvv: '111',
      pin: '1111',
      otp: '123456',
      description: 'Verve card for successful transactions.',
      type: 'success'
    },
    {
      number: '4012000033330026',
      expiry: '03/50',
      cvv: '111',
      pin: 'NA',
      otp: 'NA',
      description: 'Visa card for successful transactions',
      type: 'success'
    },
    {
      number: '4000000000002503',
      expiry: '03/50',
      cvv: '111',
      pin: 'NA',
      otp: 'NA',
      description: 'Visa card for failed transactions.',
      type: 'failed'
    },
    {
      number: '5555555555554444',
      expiry: '09/30',
      cvv: '111',
      pin: 'NA',
      otp: 'NA',
      description: 'Mastercard for successful transactions.',
      type: 'success'
    },
    {
      number: '5060990580000000390',
      expiry: '10/40',
      cvv: '111',
      pin: '1111',
      otp: '123456',
      description: 'Verve card for failed transactions (insufficient funds).',
      type: 'failed'
    },
    {
      number: '2223000000000023',
      expiry: '12/34',
      cvv: '123',
      pin: 'NA',
      otp: '',
      description: 'Frictionless (2DS)',
      type: 'frictionless'
    }
  ];

  const getStatusIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'frictionless':
        return <AlertCircle className="h-4 w-4 text-blue-500" />;
      default:
        return null;
    }
  };

  const getStatusColor = (type: string) => {
    switch (type) {
      case 'success':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'frictionless':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-12">
        <h1 className="text-3xl font-bold text-white mb-4">Environments</h1>
        
        {/* Sandbox Environment */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <TestTube className="h-6 w-6 text-yellow-500 mr-3" />
            <h2 className="text-xl font-semibold text-white">Sandbox Environment</h2>
          </div>
          <p className="text-white-muted mb-6 leading-relaxed">
            The Sandbox environment is used for testing and development. It simulates transactions without processing actual payments, 
            allowing you to refine your integration before going live.
          </p>
          <p className="text-white-muted mb-6">
            Here you can use a variety of test cards to simulate different payment scenarios and ensure your integration handles them effectively.
          </p>

          {/* Test Cards Section */}
          <div className="mb-8">
            <div className="flex items-center mb-4">
              <CreditCard className="h-5 w-5 text-lime mr-2" />
              <h3 className="text-lg font-semibold text-white">Test Cards</h3>
            </div>
            
            <div className="bg-white/5 border border-white/10 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-white/5">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Card Number</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Expiry Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">CVV</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">PIN</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">OTP</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10">
                    {testCards.map((card, index) => (
                      <tr key={index} className="hover:bg-white/5">
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-mono text-lime">{card.number}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-mono text-white-muted">{card.expiry}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-mono text-white-muted">{card.cvv}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-mono text-white-muted">{card.pin}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-mono text-white-muted">{card.otp}</td>
                        <td className="px-4 py-4 text-sm text-white-muted">
                          <div className="flex items-center">
                            {getStatusIcon(card.type)}
                            <span className="ml-2">{card.description}</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Sandbox URL */}
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-6 mb-8">
            <h3 className="text-lg font-semibold text-yellow-500 mb-3">Sandbox URL</h3>
            <div className="bg-dark/50 rounded-lg p-4 border border-yellow-500/20">
              <code className="text-yellow-400 font-mono text-sm break-all">
                https://sandbox-api.nectargateway.com/
              </code>
            </div>
          </div>
        </div>

        {/* Live Environment */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <Globe className="h-6 w-6 text-green-500 mr-3" />
            <h2 className="text-xl font-semibold text-white">Live Environment</h2>
          </div>
          <p className="text-white-muted mb-6 leading-relaxed">
            The Live environment processes real transactions and actual customer data. Ensure thorough testing and readiness before transitioning.
          </p>

          {/* Live URL */}
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-6 mb-8">
            <h3 className="text-lg font-semibold text-green-500 mb-3">Live URL</h3>
            <div className="bg-dark/50 rounded-lg p-4 border border-green-500/20">
              <code className="text-green-400 font-mono text-sm break-all">
                https://api.nectargateway.com/
              </code>
            </div>
          </div>
        </div>

        {/* What's Next */}
        <div className="bg-lime text-dark rounded-lg p-6 text-center">
          <h2 className="text-xl font-bold mb-3">What's Next</h2>
          <p className="text-dark/90 mb-4">
            Continue with 3DS card charging for secure transactions with authentication
          </p>
          <Link
            to="/server-to-server/charge-card-3ds"
            className="inline-flex items-center px-6 py-2 bg-dark text-white font-medium rounded-lg hover:bg-dark/90 transition-colors"
          >
          Charge Card with 3DS
          </Link>
        </div>
      </div>
    </div>
  );
};